import React, {Component} from 'react';

import {Parser} from 'html-to-react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import AccordionUi from 'Frontend/components/AccordionUi';
import {ModuleMargin} from 'Frontend/components/ModuleMargins';

import {parseToUri} from 'Admin/utils/formParsers';
import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';
import {isValidNode, processingInstructions} from 'Frontend/utils/parserInstructions';

/**
 * Calculates the corresponding size.
 *
 * @param {String}  width     The with defined in Backend.
 * @param {Boolean} isInGroup An flag if module is in an group.
 * @param {String}  device    Device flag.
 * @returns {String} The width.
 */
const getSize = (width, isInGroup, device) => {
    if (device === 'm' || isInGroup) {
        return '100%';
    } else if (device === 't') {
        return (width === '100%') ? width : '50%';
    }

    return width;
};

/**
 * Accordion
 *
 * @property {*} prop Prop comment.
 *
 * @class Accordion
 * @extends {Component}
 */
class Accordion extends Component {
    static propTypes = {
        headline: PropTypes.string.isRequired,
        position: PropTypes.string.isRequired,
        width: PropTypes.string.isRequired,
        accordions: PropTypes.arrayOf(
            PropTypes.shape({
                accordionName: PropTypes.string,
                copy: PropTypes.string
            })
        ),
        copy: PropTypes.string,
        isInGroup: PropTypes.bool,
        name: PropTypes.string
    }

    static defaultProps = {
        accordions: [],
        copy: null,
        isInGroup: false,
        name: null
    }

    htmlParser = new Parser();

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof Accordion
     */
    render() {
        const {
            accordions,
            copy,
            headline,
            isInGroup,
            name,
            position,
            width
        } = this.props;

        return (
            <AccordionElement isInGroup={isInGroup} position={position}>
                {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
                <a id={parseToUri(name)} />
                <AccordionContainer isInGroup={isInGroup} width={width}>
                    <Headline position={position}>
                        {headline}
                    </Headline>
                    {copy && (
                        <Text position={position}>
                            {this.htmlParser.parseWithInstructions(
                                `<div data-module="Accordion">${copy}</div>`,
                                isValidNode,
                                processingInstructions
                            )}
                        </Text>
                    )}
                    {accordions.map(accordion => {
                        const {accordionName, copy: accordionBody} = accordion;

                        return (
                            <AccordionUi
                                key={accordionName}
                                accordionBody={accordionBody}
                                accordionName={accordionName}
                            />
                        );
                    })}
                </AccordionContainer>
            </AccordionElement>
        );
    }
}

export default Accordion;

const AccordionElement = styled(ModuleMargin)`
    align-items: ${({position}) => position};
    display: flex;
    flex-direction: column;
    height: 100%;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    position: relative;
    width: max(290px, ${pxToVw(327, 'm')});

    ${MEDIA.DESKTOP.UP} {
        max-width: 100%;
        padding: ${({isInGroup}) => (isInGroup ? `0 max(110px, ${pxToVw(110, 'd')}) 0 max(110px, ${pxToVw(110, 'd')})` : '15px')};
        width: max(1320px, ${pxToVw(1320, 'd')});
    }
`;

const AccordionContainer = styled.div`
    width: ${({isInGroup, width}) => getSize(width, isInGroup, 'm')};

    ${MEDIA.TABLET.UP} {
        width: ${({isInGroup, width}) => getSize(width, isInGroup, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        width: ${({isInGroup, width}) => getSize(width, isInGroup, 'd')};
    }
`;

const Headline = styled.h3`
    ${FONTS.LIGHT}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(20, 'm')};
    letter-spacing: ${clampCss(1.2, 'm')};
    line-height: ${clampCss(24, 'm')};
    margin: 0;
    text-align: left;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(20, 't')};
        letter-spacing: ${clampCss(1.2, 't')};
        line-height: ${clampCss(24, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(20px, ${pxToVw(20, 'd')});
        letter-spacing: max(1.2px, ${pxToVw(1.2, 'd')});
        line-height: max(24px, ${pxToVw(24, 'd')});
    }
`;

const Text = styled.div`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    margin-top: ${clampCss(24, 'm')};
    text-align: left;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
        margin-top: ${clampCss(24, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(14px, ${pxToVw(14, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(19.6px, ${pxToVw(19.6, 'd')});
        margin-top: max(32px, ${pxToVw(32, 'd')});
    }
`;